/**
 * Configuration the image cannot contain.
 *
 * WHY THIS IS FETCHED AND NOT BUILT IN.
 *
 * The portal must send Medplum a `clientId` on sign-in: its users are INVITED
 * into a project, and Medplum finds a project-scoped user only when the lookup
 * is scoped to that project. Omitting it fails with `400 "User not found"` —
 * the same message the clinician app gets for the opposite reason.
 *
 * That client id is generated on the host by `deploy/host/bootstrap-medplum.sh`
 * and lives in `/opt/glycemia/.env`. It is not known when the image is built,
 * and it changes if the ClientApplication is ever recreated. Baking it in with
 * a Vite `define` would produce an image that is correct until the day it
 * silently is not — and the failure would be a login screen that rejects every
 * patient, with a message pointing at their password.
 *
 * So the container writes `/config.json` at start-up from its environment and
 * the app reads it before rendering. The client id is not a secret: it is a
 * public OAuth client identifier, sent in the clear on every login.
 *
 * Loading happens ONCE, before the first render, so everything downstream is
 * synchronous and no component has to handle a half-configured app.
 */

export interface PortalConfig {
  /** The ClientApplication whose project the portal's users belong to. */
  readonly medplumClientId: string | null;
}

let loaded: PortalConfig | null = null;

/**
 * Read `/config.json`.
 *
 * A missing or malformed file yields a null client id rather than throwing.
 * The sign-in screen then says plainly that the portal is not configured,
 * which is a true and actionable message — far better than letting every
 * patient believe their password is wrong.
 */
export const loadConfig = async (): Promise<PortalConfig> => {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' });
    if (!response.ok) {
      loaded = { medplumClientId: null };
      return loaded;
    }
    const body = (await response.json()) as { medplumClientId?: unknown };
    const clientId = typeof body.medplumClientId === 'string' ? body.medplumClientId : '';
    loaded = { medplumClientId: clientId === '' ? null : clientId };
    return loaded;
  } catch {
    loaded = { medplumClientId: null };
    return loaded;
  }
};

/**
 * The configuration, after `loadConfig`.
 *
 * Throws if called first — a programmer error, not a runtime condition, so it
 * is an exception rather than a `Result` (§17.1).
 */
export const getConfig = (): PortalConfig => {
  if (loaded === null) {
    throw new Error('loadConfig() must resolve before the app reads its configuration.');
  }
  return loaded;
};
